// apps/web/src/pages/compras/importar-nota-dialog.tsx — Fase 13, Plano 22 (COMP-03, D-15, T-13-22B/C).
// Diálogo "Importar NF de compra": 1..N arquivos XML, um POST por arquivo (/api/v1/compras/notas { xmlBase64 }), resultado por
// arquivo. O XML é lido como Base64 e NUNCA interpretado no navegador (o parse, o destinatário e a chave são do servidor).
// "Ir para a conferência" só aparece quando exatamente 1 NF foi importada.
import { useRef, useState, type ChangeEvent } from 'react';
import { AlertCircle, CheckCircle2, FileText, Loader2, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  arquivoParaBase64,
  destinoAposImportacao,
  resultadoDaImportacao,
  resultadoDeFalha,
  urlConferencia,
  type ResultadoArquivo,
} from '../../lib/compras/importacao-xml';
import { navegar } from '../../lib/compras/navegar';

interface ArquivoEstado {
  readonly chave: string;
  readonly nome: string;
  /** null = ainda lendo/enviando. */
  readonly resultado: ResultadoArquivo | null;
}

export interface ImportarNotaDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** Chamado quando ao menos uma NF foi importada (a lista recarrega). */
  readonly aoImportar: () => void;
}

export function ImportarNotaDialog({ aberto, onAbertoChange, aoImportar }: ImportarNotaDialogProps) {
  const [selecionados, setSelecionados] = useState<readonly File[]>([]);
  const [arquivos, setArquivos] = useState<readonly ArquivoEstado[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [concluido, setConcluido] = useState(false);
  const campoRef = useRef<HTMLInputElement>(null);

  function reiniciar(): void {
    setSelecionados([]);
    setArquivos([]);
    setEnviando(false);
    setConcluido(false);
    if (campoRef.current) campoRef.current.value = '';
  }

  function alterarAberto(proximo: boolean): void {
    if (!proximo && enviando) return;
    if (!proximo) reiniciar();
    onAbertoChange(proximo);
  }

  function aoEscolher(evento: ChangeEvent<HTMLInputElement>): void {
    setSelecionados(Array.from(evento.target.files ?? []));
    setArquivos([]);
    setConcluido(false);
  }

  function registrarResultado(chave: string, resultado: ResultadoArquivo): void {
    setArquivos((atual) => atual.map((arquivo) => (arquivo.chave === chave ? { ...arquivo, resultado } : arquivo)));
  }

  async function importar(): Promise<void> {
    if (selecionados.length === 0 || enviando) return;
    const lote = selecionados.map((arquivo, indice) => ({ arquivo, chave: `${indice}-${arquivo.name}` }));
    setEnviando(true);
    setConcluido(false);
    setArquivos(lote.map(({ arquivo, chave }) => ({ chave, nome: arquivo.name, resultado: null })));
    let algumaImportada = false;
    // Um POST por arquivo, em sequência: o servidor reserva a chave sob lock e a ordem dos resultados fica previsível.
    for (const { arquivo, chave } of lote) {
      try {
        const xmlBase64 = await arquivoParaBase64(arquivo);
        const resposta = await apiMutate('/api/v1/compras/notas', { method: 'POST', body: { xmlBase64 } });
        const corpo: unknown = await resposta.json().catch(() => null);
        const resultado = resultadoDaImportacao(resposta.status, corpo);
        if (resultado.tipo === 'ok') algumaImportada = true;
        registrarResultado(chave, resultado);
      } catch {
        registrarResultado(chave, resultadoDeFalha());
      }
    }
    setEnviando(false);
    setConcluido(true);
    if (algumaImportada) aoImportar();
  }

  const resultados = arquivos.flatMap((arquivo) => (arquivo.resultado ? [arquivo.resultado] : []));
  const unicaImportada = concluido && destinoAposImportacao(resultados) === 'conferencia' ? resultados.find((resultado) => resultado.tipo === 'ok') : undefined;

  return (
    <Dialog open={aberto} onOpenChange={alterarAberto}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Importar NF de compra</DialogTitle>
          <DialogDescription>Selecione o XML da nota do fornecedor para conferir e dar entrada no estoque.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="importar-nota-arquivos">Arquivo(s) XML</Label>
          <input
            ref={campoRef}
            id="importar-nota-arquivos"
            type="file"
            accept=".xml"
            multiple
            disabled={enviando}
            onChange={aoEscolher}
            className="ui-control block w-full rounded-md border border-slate-300 bg-white text-sm file:mr-3 file:min-h-[var(--control-height,2rem)] file:border-0 file:bg-slate-100 file:px-3 file:text-sm file:font-medium"
          />
          <p className="text-sm text-muted-foreground">Formato aceito: XML da NF-e (.xml).</p>
        </div>

        {arquivos.length > 0 && (
          <ul className="flex flex-col gap-2" aria-live="polite" aria-label="Resultado por arquivo">
            {arquivos.map((arquivo) => (
              <li key={arquivo.chave} className="flex flex-col gap-1 rounded-md border px-3 py-2 text-sm">
                <span className="flex items-center gap-2 font-medium">
                  <FileText className="size-4 shrink-0 text-slate-500" aria-hidden="true" />
                  <span className="truncate">{arquivo.nome}</span>
                </span>
                <ResultadoDoArquivo resultado={arquivo.resultado} />
              </li>
            ))}
          </ul>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="secondary" disabled={enviando} onClick={() => alterarAberto(false)}>
            Voltar
          </Button>
          <div className="flex flex-wrap gap-2">
            {unicaImportada?.notaId && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const destino = urlConferencia(unicaImportada.notaId ?? '');
                  reiniciar();
                  onAbertoChange(false);
                  navegar(destino);
                }}
              >
                Ir para a conferência
              </Button>
            )}
            <Button type="button" disabled={selecionados.length === 0 || enviando} onClick={() => void importar()}>
              {enviando ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Upload aria-hidden="true" />}
              Importar XML
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultadoDoArquivo({ resultado }: { resultado: ResultadoArquivo | null }) {
  if (resultado === null) {
    return (
      <span className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Lendo XML…
      </span>
    );
  }
  if (resultado.tipo === 'ok') {
    return (
      <span className="flex items-center gap-2 text-emerald-700">
        <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
        {resultado.notaId ? (
          <a href={urlConferencia(resultado.notaId)} className="underline underline-offset-2">
            {resultado.mensagem}
          </a>
        ) : (
          resultado.mensagem
        )}
      </span>
    );
  }
  const duplicada = resultado.tipo === 'duplicada';
  return (
    <span className={`flex flex-wrap items-center gap-2 ${duplicada ? 'text-amber-700' : 'text-rose-700'}`}>
      <AlertCircle className="size-4 shrink-0" aria-hidden="true" />
      <span>{resultado.mensagem}</span>
      {duplicada && resultado.notaId && (
        <Button asChild variant="outline" size="sm">
          <a href={urlConferencia(resultado.notaId)}>Abrir</a>
        </Button>
      )}
    </span>
  );
}
